/**
 * SideBySideView Component
 * Side-by-side comparison with radar chart and grouped bars
 */

import { useMemo } from 'react'
import { useComparisonStore } from '@/store'
import { Card } from '@/components/common'
import { RadarChart, GroupedBarChart, TimeSeriesChart } from '@/components/charts'
import { AVAILABLE_KPIS } from '@/types/comparison'

interface SideBySideViewProps {
  showTrends?: boolean
}

// Color palette for assets
const ASSET_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
]

export default function SideBySideView({ showTrends = true }: SideBySideViewProps) {
  const { kpiComparison, trendComparison, filters } = useComparisonStore()

  // Prepare radar chart data
  const radarData = useMemo(() => {
    if (!kpiComparison?.assets) return { data: [], categories: [] }

    // Get selected KPIs or default set
    const selectedKPIs = filters.kpis.length > 0
      ? filters.kpis
      : ['reliability', 'availability', 'mtbf', 'mttr', 'health_score']

    // Map KPI ids to labels
    const categories = selectedKPIs.map(kpiId => {
      const kpi = AVAILABLE_KPIS.find(k => k.id === kpiId)
      return kpi?.label || kpiId
    })

    // Normalize values to 0-100 scale for radar chart
    const normalizeValue = (value: number, kpiId: string) => {
      switch (kpiId) {
        case 'reliability':
        case 'availability':
        case 'health_score':
          return value // Already 0-100
        case 'mtbf':
          return Math.min((value / 1000) * 100, 100) // Normalize to 0-1000 scale
        case 'mttr':
          return Math.max(100 - (value / 20) * 100, 0) // Inverse - lower is better
        case 'anomaly_rate':
          return Math.max(100 - value * 10, 0) // Inverse - lower is better
        default:
          return value
      }
    }

    const data = kpiComparison.assets.map((asset, index) => ({
      name: asset.asset_id,
      values: selectedKPIs.map(kpiId => {
        const rawValue = asset.metrics[kpiId as keyof typeof asset.metrics]
        return normalizeValue(rawValue ?? 0, kpiId)
      }),
      color: ASSET_COLORS[index % ASSET_COLORS.length]
    }))

    return { data, categories }
  }, [kpiComparison, filters.kpis])

  // Prepare grouped bar chart data
  const barData = useMemo(() => {
    if (!kpiComparison?.assets) return { data: [], categories: [] }

    const categories = ['Reliability %', 'Availability %', 'Health Score %']

    const data = kpiComparison.assets.map((asset, index) => ({
      name: asset.asset_id,
      values: [
        asset.metrics.reliability,
        asset.metrics.availability,
        asset.metrics.health_score
      ],
      color: ASSET_COLORS[index % ASSET_COLORS.length]
    }))

    return { data, categories }
  }, [kpiComparison])

  // Prepare trend comparison data
  const trendData = useMemo(() => {
    if (!trendComparison?.trends) return []

    return Object.entries(trendComparison.trends).map(([assetId, metrics], index) => ({
      x: trendComparison.timestamps,
      y: metrics.reliability || [],
      name: assetId,
      color: ASSET_COLORS[index % ASSET_COLORS.length]
    }))
  }, [trendComparison])

  if (!kpiComparison || kpiComparison.assets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-surface-400">
        Select assets to compare their KPIs side by side
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-12 gap-4">
        {/* Radar Chart */}
        <div className="col-span-12 lg:col-span-6">
          <Card title="KPI Spider Chart" subtitle="Multi-dimensional comparison">
            <RadarChart
              data={radarData.data}
              categories={radarData.categories}
              height={350}
              showLegend={true}
            />
          </Card>
        </div>

        {/* Grouped Bar Chart */}
        <div className="col-span-12 lg:col-span-6">
          <Card title="KPI Comparison" subtitle="Side-by-side bar comparison">
            <GroupedBarChart
              data={barData.data}
              categories={barData.categories}
              height={350}
              showLegend={true}
              yAxisTitle="Percentage"
            />
          </Card>
        </div>

        {/* Asset Summary Cards */}
        <div className="col-span-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {kpiComparison.assets.map((asset, index) => (
              <Card key={asset.asset_id} className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: ASSET_COLORS[index % ASSET_COLORS.length] }}
                  />
                  <span className="font-mono text-primary-400 font-medium">{asset.asset_id}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-surface-400">Reliability</span>
                    <span className="font-mono text-surface-200">{asset.metrics.reliability.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Health Score</span>
                    <span className="font-mono text-surface-200">{asset.metrics.health_score.toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">MTBF</span>
                    <span className="font-mono text-surface-200">{asset.metrics.mtbf.toFixed(0)}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Anomaly Rate</span>
                    <span className="font-mono text-surface-200">{asset.metrics.anomaly_rate.toFixed(1)}%</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Trend Comparison */}
        {showTrends && trendData.length > 0 && (
          <div className="col-span-12">
            <Card title="Reliability Trends" subtitle="Historical comparison over time">
              <TimeSeriesChart
                data={trendData}
                height={300}
                yAxisTitle="Reliability %"
                showLegend={true}
              />
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
